'use client'

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

// Canvas field of small pixel-squares with randomized opacity — the same texture
// used in the "Design and Development" card. Fills its container; apply a
// mask-image on the wrapper (e.g. a radial gradient) to shape it.
//
// Pass `animate` to make every pixel gently shimmer — each cell breathes on its
// own phase/speed for a living texture. Rendering is bucketed by opacity so even
// a full-bleed 4K field stays cheap, and it falls back to a static render when
// the user prefers reduced motion.
export function SquareDots({
  className = '',
  color = '#ffffff',
  squareSize = 4,
  gap = 4,
  animate = false,
  speed = 1,
}) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return undefined
    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined

    // Resolve the CSS color to r/g/b once (not per frame).
    const probe = document.createElement('div')
    probe.style.color = color
    document.body.appendChild(probe)
    const computed = getComputedStyle(probe).color
    document.body.removeChild(probe)
    const rgb = computed.match(/\d+/g)
    const r = rgb ? parseInt(rgb[0], 10) : 255
    const g = rgb ? parseInt(rgb[1], 10) : 255
    const b = rgb ? parseInt(rgb[2], 10) : 255

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const isAnimated = animate && !reduceMotion

    const step = squareSize + gap
    const MAX_ALPHA = 0.6
    const BUCKETS = 24
    // One prebuilt fillStyle per opacity bucket, so a frame only sets ~24 styles.
    const styleLUT = Array.from(
      { length: BUCKETS },
      (_, i) => `rgba(${r}, ${g}, ${b}, ${((i + 0.5) / BUCKETS) * MAX_ALPHA})`,
    )

    let width = 0
    let height = 0
    let count = 0
    let xs = null
    let ys = null
    let bases = null
    let phases = null
    let speeds = null
    let bucketIdx = null
    let bucketCounts = null

    const rebuild = () => {
      const cols = Math.max(1, Math.ceil(width / step))
      const rows = Math.max(1, Math.ceil(height / step))
      count = cols * rows
      xs = new Float32Array(count)
      ys = new Float32Array(count)
      bases = new Float32Array(count)
      phases = new Float32Array(count)
      speeds = new Float32Array(count)

      // Deterministic seed → the base texture is identical every render.
      let seed = 12345
      const rnd = () => {
        seed = (seed * 1103515245 + 12345) & 0x7fffffff
        return seed / 0x7fffffff
      }

      let i = 0
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          xs[i] = col * step
          ys[i] = row * step
          bases[i] = rnd() * 0.5 + 0.05
          phases[i] = rnd() * Math.PI * 2
          speeds[i] = 0.35 + rnd() * 1.1
          i++
        }
      }

      bucketIdx = Array.from({ length: BUCKETS }, () => new Uint32Array(count))
      bucketCounts = new Uint32Array(BUCKETS)
    }

    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height)
      for (let i = 0; i < count; i++) {
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${bases[i]})`
        ctx.fillRect(xs[i], ys[i], squareSize, squareSize)
      }
    }

    const drawFrame = (tMs) => {
      // The rAF loop can tick before the first ResizeObserver build populates the
      // buffers — skip until the field is ready.
      if (!count || !bucketCounts) return
      const t = tMs * 0.001 * speed
      ctx.clearRect(0, 0, width, height)
      bucketCounts.fill(0)

      const scale = (BUCKETS - 1) / MAX_ALPHA
      for (let i = 0; i < count; i++) {
        const wave = 0.5 + 0.5 * Math.sin(t * speeds[i] + phases[i])
        const op = bases[i] * (0.32 + 0.68 * wave)
        let bucket = (op * scale) | 0
        if (bucket <= 0) continue
        if (bucket >= BUCKETS) bucket = BUCKETS - 1
        bucketIdx[bucket][bucketCounts[bucket]++] = i
      }

      for (let bkt = 1; bkt < BUCKETS; bkt++) {
        const n = bucketCounts[bkt]
        if (!n) continue
        const idx = bucketIdx[bkt]
        ctx.fillStyle = styleLUT[bkt]
        ctx.beginPath()
        for (let k = 0; k < n; k++) {
          const i = idx[k]
          ctx.rect(xs[i], ys[i], squareSize, squareSize)
        }
        ctx.fill()
      }
    }

    let rafId = null
    let last = 0
    let running = false
    const FRAME_MS = 1000 / 30
    const loop = (t) => {
      if (!running) return
      rafId = requestAnimationFrame(loop)
      if (t - last < FRAME_MS) return
      last = t
      drawFrame(t)
    }
    const startLoop = () => {
      if (running) return
      running = true
      last = 0
      rafId = requestAnimationFrame(loop)
    }
    const stopLoop = () => {
      running = false
      if (rafId) cancelAnimationFrame(rafId)
    }

    const applySize = (w, h) => {
      // Cap DPR: the backing store and per-frame clear cost scale with dpr², so an
      // animated full-bleed field is capped tighter than a draw-once static one.
      const dpr = Math.min(window.devicePixelRatio || 1, isAnimated ? 1.5 : 2)
      width = w
      height = h
      canvas.width = Math.max(1, Math.floor(w * dpr))
      canvas.height = Math.max(1, Math.floor(h * dpr))
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      rebuild()
      if (isAnimated) {
        if (running) drawFrame(performance.now())
      } else {
        drawStatic()
      }
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect
        if (w > 0 && h > 0) applySize(w, h)
      }
    })
    resizeObserver.observe(container)

    // Only run the animation loop while the field is on (or near) screen.
    let visibilityObserver = null
    if (isAnimated) {
      visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) startLoop()
          else stopLoop()
        },
        { rootMargin: '200px 0px' },
      )
      visibilityObserver.observe(container)
    }

    return () => {
      resizeObserver.disconnect()
      if (visibilityObserver) visibilityObserver.disconnect()
      stopLoop()
    }
  }, [color, squareSize, gap, animate, speed])

  return (
    <div
      ref={containerRef}
      className={cn('pointer-events-none select-none', className)}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  )
}
